/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ContextualMenuItemType, IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { getId } from '@fluentui/react/lib/Utilities';
import React from 'react';
import { PredictedAsMenuItem } from 'src/luis/modules/labeling/components/PredictedAsMenuItem';
import { EntityDropdownSelection } from 'src/luis/modules/labeling/types/EntityDropdownProps';
import { MenuActionItems } from 'src/luis/modules/labeling/utils/entityDropdown/menuActionsGenerator';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { isMachineLearned } from 'src/luis/projects/entities/utils/entityHelpers';

/**
 * The parameters passed to the entity dropdown/menu items generators.
 */
type ItemsGeneratorsParams = {
    /**
     * List of all entities (filtered & mapped to IContextualMenuItem type).
     */
    generatedEntities: IContextualMenuItem[];

    /**
     * The user selected entity.
     */
    entitySelection: EntityDropdownSelection;

    /**
     * An object with all actions.
     */
    actions: MenuActionItems;

    /**
     * Determines whether the selected tokens have predictions or not.
     */
    havePredictions: boolean;

    /**
     * The entities that are used as labels.
     */
    labeledEntities: Entity[];

    /**
     * Menu actions config.
     */
    config: {
        enableEntityPaneAction: boolean;
        enablePrebuiltModalAction: boolean;
    };
};

/**
 * A utility function that returns the menu items to display for a selected labeled entity.
 */
export const getMenuActionItems = (params: ItemsGeneratorsParams): IContextualMenuItem[] => {
    const { generatedEntities, entitySelection, actions, havePredictions, labeledEntities, config } = params;
    const confirmPredictionAction = havePredictions ? [generateDivider(), actions.confirmPredictionAction()] : [];

    let unLabelAction: IContextualMenuItem[] = [];

    if (entitySelection?.clickedOnLabel?.role && !isMachineLearned(entitySelection.clickedOnLabel.entity.typeId)) {
        // Unlabel the list entity with roles which isn't included in the labeled entities.
        unLabelAction = [actions.unLabelAction([entitySelection.clickedOnLabel.entity])];
    } else if (labeledEntities.length) {
        unLabelAction = [actions.unLabelAction(labeledEntities)];
    }

    if (entitySelection?.kind === 'prediction') {
        const parentEntityName = entitySelection.selection[0].name;
        const subentitiesPredictions = entitySelection.selection.length > 1 ? [actions.confirmSubEntitiesPredictionAction(parentEntityName)] : [];

        return [
            {
                key: 'predictedAs',
                text: '',
                onRender: () => <PredictedAsMenuItem entity={entitySelection.selection[0]} />
            },
            generateDivider(),
            actions.confirmPredictionAction(entitySelection.selection[0].name),
            ...subentitiesPredictions
        ];
    }

    return [
        ...generatedEntities,
        generateDivider(),
        ...(config.enableEntityPaneAction ? [actions.entityPaneAction('view')] : []),
        generateDivider(),
        ...(config.enablePrebuiltModalAction ? [actions.openPrebuiltEntityModalAction] : []),
        ...confirmPredictionAction,
        ...unLabelAction
    ].filter(Boolean);
};

export const generateDivider: () => IContextualMenuItem = () => ({
    key: getId('divider'),
    itemType: ContextualMenuItemType.Divider
});
